import { useMemo, useState, type FormEvent, type JSX } from 'react';
import type { MonitorState, NetworkNode, NodeKind, Topology } from '@netdeck/contracts';
import { Button } from '../../shared/ui/Button';
import { DataTable, type Column } from '../../shared/ui/DataTable';
import { Field, FormActions, FormRow } from '../../shared/ui/Field';
import { Icon } from '../../shared/ui/Icon';
import { useTranslation } from '../../shared/i18n';
import { useErrorText } from '../../shared/i18n/useErrorText';
import { NODE_KIND_LABEL_KEYS, NodeIcon } from '../../shared/ui/NodeIcon';
import { EmptyState, ErrorState, Loading } from '../../shared/ui/Panel';
import { StatusDot, type Status } from '../../shared/ui/StatusDot';
import { useAgents } from '../agents/api';
import type { NodeVncAccess } from '../vnc/access';
import type { NodeSshAccess } from '../ssh/node-access';
import type { NodeVideoAccess } from '../video/access';
import { Sheet } from '../../shared/ui/Sheet';
import { InfoNote } from '../../shared/ui/InfoNote';
import { useIsPhone } from '../../shared/ui/viewport';
import { NodePanel } from './NodePanel';
import { showsExtraAddresses } from './extraAddresses';
import { combineStatuses, monitorsSummary, statesByNode } from './status';
import { useCreateNode, useDeleteNode, useSites, useTopology } from './api';
import { useLiveStatus } from './useLiveStatus';

/** Фильтр по состоянию — как в шапке раздела 04 макета. */
type StatusFilter = 'all' | Status;

/**
 * Экран «Узлы и мониторы» — раздел 04 макета.
 *
 * Карта сети (раздел 03) вне объёма фазы 3: ТЗ прямо говорит, что «пока
 * хватает списка узлов». Плотная таблица — это не заглушка вместо карты,
 * а основной инструмент: на упавшей площадке инженеру нужен список того,
 * что не отвечает, а не красивая схема.
 */
/** Лучшее из измеренных времён отклика: показывать сумму бессмысленно. */
/** Адреса одного узла из схемы площадки. */
function addressesOf(
  addresses: Topology['addresses'] | undefined,
  nodeId: string,
): Topology['addresses'] {
  return (addresses ?? []).filter((address) => address.nodeId === nodeId);
}

function bestRtt(states: readonly MonitorState[]): number | null {
  const values = states
    .map((state) => state.rttMs)
    .filter((value): value is number => value !== null);

  return values.length === 0 ? null : Math.min(...values);
}

export function NodesPage({
  canWrite,
  vnc,
  ssh,
  video,
}: {
  canWrite: boolean;
  /** Доступ к VNC. Не передан — раздела VNC в панели узла нет. */
  vnc?: NodeVncAccess;
  ssh?: NodeSshAccess;
  video?: NodeVideoAccess;
}): JSX.Element {
  const { t } = useTranslation();
  const errorText = useErrorText();

  const sites = useSites();
  const [siteId, setSiteId] = useState<string | null>(null);

  const activeSiteId = siteId ?? sites.data?.[0]?.id ?? null;
  const topology = useTopology(activeSiteId);
  const live = useLiveStatus(activeSiteId);
  const agents = useAgents();

  /**
   * Проверки выполняет агент площадки. Без него мониторы можно завести,
   * но выполнять их некому — и об этом нужно сказать прямо, иначе узел
   * молча остаётся серым, а причина ниоткуда не следует.
   */
  const siteAgents = (agents.data ?? []).filter((agent) =>
    agent.sites.some((site) => site.id === activeSiteId),
  );
  const agentOnline = siteAgents.some((agent) => agent.status === 'online');

  const createNode = useCreateNode(activeSiteId);
  const deleteNode = useDeleteNode(activeSiteId);

  const [selectedNodeId, setSelectedNodeId] = useState<string | null>(null);
  const [adding, setAdding] = useState(false);
  const [filter, setFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('all');

  const nodes = topology.data?.nodes ?? [];

  /** Состояние узла — сводка по его мониторам (§11, фаза 4, задача 2). */
  const statusByNode = useMemo(() => {
    const grouped = statesByNode(live.monitors);

    const result = new Map<string, { status: Status; rttMs: number | null; count: number }>();
    for (const [nodeId, states] of grouped) {
      result.set(nodeId, {
        status: combineStatuses(states),
        rttMs: bestRtt(states),
        count: states.length,
      });
    }
    return result;
  }, [live.monitors]);

  /** Сколько у узла дополнительных адресов — для пометки в колонке адреса. */
  const addressCountByNode = useMemo(() => {
    const byNode = new Map<string, number>();

    for (const address of topology.data?.addresses ?? []) {
      byNode.set(address.nodeId, (byNode.get(address.nodeId) ?? 0) + 1);
    }

    return byNode;
  }, [topology.data?.addresses]);

  const counts = useMemo(() => {
    const totals = { up: 0, down: 0, warn: 0, unknown: 0 };
    for (const node of nodes) {
      totals[statusByNode.get(node.id)?.status ?? 'unknown'] += 1;
    }
    return totals;
  }, [nodes, statusByNode]);

  const visible = useMemo(() => {
    const needle = filter.trim().toLowerCase();

    return nodes.filter((node) => {
      const status = statusByNode.get(node.id)?.status ?? 'unknown';
      if (statusFilter !== 'all' && status !== statusFilter) {
        return false;
      }
      if (!needle) {
        return true;
      }
      return (
        node.name.toLowerCase().includes(needle) ||
        (node.address ?? '').toLowerCase().includes(needle)
      );
    });
  }, [nodes, filter, statusFilter, statusByNode]);

  const selectedNode = nodes.find((node) => node.id === selectedNodeId) ?? visible[0] ?? null;

  const phone = useIsPhone();

  /*
    Шторку открывает выбор, сделанный руками, а не подстановка первого узла:
    иначе экран узлов на телефоне открывался бы сразу закрытым — поверх
    списка стояла бы панель первой строки, которую никто не просил.
  */
  const openedNode = selectedNodeId === null ? null : (selectedNode ?? null);

  const columns: Column<NetworkNode>[] = [
    {
      key: 'status',
      header: '',
      width: '20px',
      /*
        Порядок состояний, а не их названия по алфавиту: сортируют этот
        столбец, чтобы собрать наверху сломанное, — «down» должен идти
        первым, а не после «unknown» по букве «d».
      */
      sortValue: (node) => STATUS_ORDER[statusByNode.get(node.id)?.status ?? 'unknown'],
      render: (node) => {
        const status = statusByNode.get(node.id)?.status ?? 'unknown';
        return <StatusDot status={status} label={t(STATUS_LABEL_KEYS[status])} />;
      },
    },
    {
      key: 'name',
      header: t('events.column.node'),
      width: '1.4fr',
      sortValue: (node) => node.name,
      render: (node) => <span className="truncate font-medium">{node.name}</span>,
    },
    {
      key: 'address',
      header: t('nodes.field.address'),
      width: '1fr',
      sortValue: (node) => node.address,
      render: (node) => {
        // Дополнительные адреса (ADR 035) не перечисляются: их бывают сотни.
        // Пометка говорит, что они есть, а сам список — в панели свойств.
        // У типов, где адрес всегда один, нет и пометки.
        const extra = showsExtraAddresses(node.kind) ? (addressCountByNode.get(node.id) ?? 0) : 0;

        return (
          <span className="flex min-w-0 items-baseline gap-1.5">
            <span className="truncate font-mono text-muted">
              {node.address ?? t('common.dash')}
            </span>
            {extra > 0 ? (
              <span
                className="flex-none font-mono text-2xs text-faint"
                title={t('nodes.addresses.moreTitle', { count: extra })}
              >
                {t('nodes.addresses.more', { count: extra })}
              </span>
            ) : null}
          </span>
        );
      },
    },
    {
      key: 'kind',
      header: t('nodes.field.kind'),
      width: '0.9fr',
      // По названию типа, а не по коду: в списке видно «Коммутатор»,
      // и сортировка обязана совпадать с тем, что человек читает.
      sortValue: (node) => t(NODE_KIND_LABEL_KEYS[node.kind]),
      render: (node) => (
        <span className="flex items-center gap-1.5 text-muted">
          <NodeIcon kind={node.kind} />
          {t(NODE_KIND_LABEL_KEYS[node.kind])}
        </span>
      ),
    },
    {
      key: 'monitors',
      header: t('monitors.title'),
      width: '1.4fr',
      // По числу проверок: «сколько их у узла» — вопрос, ради которого
      // эту колонку и сортируют. Узлы без мониторов уходят вниз сами:
      // отсутствие сводки читается как пустое значение.
      sortValue: (node) => statusByNode.get(node.id)?.count,
      render: (node) => {
        const summary = statusByNode.get(node.id);
        if (!summary) {
          return <span className="font-mono text-xs text-dim">{t('nodes.noMonitor')}</span>;
        }
        return (
          <span
            className={`font-mono text-xs ${summary.status === 'down' ? 'text-down-fg' : 'text-muted'}`}
          >
            {monitorsSummary(summary.count, summary.status, t)}
          </span>
        );
      },
    },
    {
      key: 'rtt',
      header: t('nodes.column.rtt'),
      width: '0.7fr',
      sortValue: (node) => statusByNode.get(node.id)?.rttMs ?? null,
      render: (node) => {
        const rtt = statusByNode.get(node.id)?.rttMs ?? null;
        return (
          <span className="font-mono text-muted">
            {rtt === null ? t('common.dash') : rtt.toFixed(1)}
          </span>
        );
      },
    },
    {
      key: 'actions',
      header: '',
      width: '44px',
      align: 'right',
      render: (node) =>
        canWrite ? (
          <Button
            icon="trash"
            size="sm"
            variant="danger"
            disabled={deleteNode.isPending}
            aria-label={t('nodes.deleteAria', { name: node.name })}
            title={t('nodes.deleteTitle')}
            onClick={(event) => {
              event.stopPropagation();
              if (confirm(t('nodes.deleteConfirm', { name: node.name }))) {
                deleteNode.mutate(node.id);
              }
            }}
          />
        ) : (
          <span className="font-mono text-xs text-faint">—</span>
        ),
    },
  ];

  if (sites.isPending) {
    return <Loading text={t('sites.loading')} />;
  }

  if (activeSiteId === null) {
    return <EmptyState title={t('sites.emptyTitle')} hint={t('nodes.noSitesHint')} />;
  }

  return (
    <div className="flex min-h-0 flex-1 flex-col p-4">
      <div className="grid min-h-0 flex-1 grid-cols-1 overflow-hidden rounded-lg border border-line bg-surface xl:grid-cols-[1fr_420px]">
        <div className="flex min-h-0 flex-col border-line xl:border-r">
          {/*
            Высота по содержимому, а не ровно 44 px.

            Перенос строк здесь был и раньше, а высота оставалась фиксированной
            — и вторая строка с фильтрами и кнопкой «Добавить узел» просто
            вылезала за шапку, где её накрывало следующим блоком. На телефоне
            это выглядело как «предупреждение закрыло кнопки».
          */}
          <header className="flex min-h-11 flex-none flex-wrap items-center gap-2.5 gap-y-1.5 border-b border-line bg-raised px-3.5 py-1.5">
            <h2 className="text-lg font-semibold">{t('nodes.title')}</h2>

            <select
              value={activeSiteId}
              onChange={(event) => {
                setSiteId(event.target.value);
                setSelectedNodeId(null);
              }}
              aria-label={t('sites.one')}
              className="h-6.5 rounded-sm border border-line-strong bg-surface px-1.5 text-sm text-fg"
            >
              {(sites.data ?? []).map((site) => (
                <option key={site.id} value={site.id}>
                  {site.name}
                </option>
              ))}
            </select>

            <span className="font-mono text-xs text-dim">{nodes.length}</span>

            <div className="flex gap-1.5">
              <StatusChip
                status="up"
                count={counts.up}
                active={statusFilter === 'up'}
                onToggle={setStatusFilter}
              />
              <StatusChip
                status="down"
                count={counts.down}
                active={statusFilter === 'down'}
                onToggle={setStatusFilter}
              />
              <StatusChip
                status="unknown"
                count={counts.unknown}
                active={statusFilter === 'unknown'}
                onToggle={setStatusFilter}
              />
            </div>

            {/*
              Группа тоже переносится и тоже не держит своей ширины: сама она
              была 399 px при экране в 360, и переносить её было нечему —
              строка внутри строки уезжала за край целиком.
            */}
            <div className="ml-auto flex min-w-0 flex-wrap items-center justify-end gap-2">
              <LiveIndicator connected={live.connected} />
              <span className="flex-none text-dim">
                <Icon name="search" />
              </span>
              {/*
                Поле сжимается до места, которое осталось, но не уже 120 px:
                в поле фильтра по имени и адресу должно помещаться хотя бы
                начало того и другого.
              */}
              <input
                value={filter}
                onChange={(event) => setFilter(event.target.value)}
                placeholder={t('nodes.filter')}
                aria-label={t('nodes.filterAria')}
                className="h-6.5 w-[180px] max-w-full min-w-[120px] flex-1 rounded-sm border border-line-strong bg-surface px-2 font-mono text-sm text-fg placeholder:text-faint"
              />
              {canWrite ? (
                <Button
                  icon={adding ? 'close' : 'plus'}
                  size="sm"
                  onClick={() => setAdding((value) => !value)}
                >
                  {adding ? t('common.action.cancel') : t('nodes.add')}
                </Button>
              ) : null}
            </div>
          </header>

          {!agents.isPending && !agentOnline ? (
            <AgentWarning hasAgent={siteAgents.length > 0} compact={phone} />
          ) : null}

          {adding ? (
            <AddNodeForm
              pending={createNode.isPending}
              error={createNode.error}
              nextPosition={nextFreeSlot(nodes)}
              onSubmit={(input) =>
                createNode.mutate(input, {
                  onSuccess: (node) => {
                    setAdding(false);
                    setSelectedNodeId(node.id);
                  },
                })
              }
            />
          ) : null}

          <div className="min-h-0 flex-1 overflow-auto">
            {topology.isPending ? (
              <Loading />
            ) : topology.error ? (
              <ErrorState
                title={t('nodes.loadFailed')}
                hint={t('nodes.loadFailedHint', { reason: errorText(topology.error) })}
              />
            ) : (
              <DataTable
                columns={columns}
                rows={visible}
                rowKey={(node) => node.id}
                rowDanger={(node) => statusByNode.get(node.id)?.status === 'down'}
                onRowClick={(node) => setSelectedNodeId(node.id)}
                selectedKey={selectedNode?.id}
                empty={
                  nodes.length === 0 ? (
                    <EmptyState
                      title={t('nodes.emptyTitle')}
                      hint={t('nodes.emptyHint')}
                      action={
                        canWrite ? (
                          <Button icon="plus" size="sm" onClick={() => setAdding(true)}>
                            {t('nodes.add')}
                          </Button>
                        ) : null
                      }
                    />
                  ) : (
                    <EmptyState title={t('nodes.filterEmpty')} hint={t('nodes.filterEmptyHint')} />
                  )
                }
              />
            )}
          </div>

          <footer className="flex h-9 flex-none items-center justify-between border-t border-line bg-row-alt px-3.5 font-mono text-xs text-dim">
            <span>{t('nodes.shownOf', { shown: visible.length, total: nodes.length })}</span>
            <span>{live.connected ? t('nodes.liveOn') : t('nodes.liveOff')}</span>
          </footer>
        </div>

        {/*
          На телефоне панель приходит шторкой поверх списка, а не колонкой
          под ним: свойства узла, оказавшиеся ниже сорока строк таблицы, —
          это ответ, до которого надо доскроллить.
        */}
        {phone ? null : selectedNode ? (
          <NodePanel
            node={selectedNode}
            groups={topology.data?.groups ?? []}
            addresses={addressesOf(topology.data?.addresses, selectedNode.id)}
            status={statusByNode.get(selectedNode.id)?.status ?? 'unknown'}
            live={live.monitors}
            canWrite={canWrite}
            vnc={vnc}
            ssh={ssh}
            video={video}
          />
        ) : (
          <div className="hidden p-3.5 text-sm text-muted xl:block">{t('nodes.pickNode')}</div>
        )}
      </div>

      {phone && openedNode ? (
        <Sheet title={openedNode.name} onClose={() => setSelectedNodeId(null)}>
          <NodePanel
            node={openedNode}
            groups={topology.data?.groups ?? []}
            addresses={addressesOf(topology.data?.addresses, openedNode.id)}
            status={statusByNode.get(openedNode.id)?.status ?? 'unknown'}
            live={live.monitors}
            canWrite={canWrite}
            vnc={vnc}
            ssh={ssh}
            video={video}
          />
        </Sheet>
      ) : null}
    </div>
  );
}

function AddNodeForm({
  pending,
  error,
  nextPosition,
  onSubmit,
}: {
  pending: boolean;
  error: Error | null;
  /** Куда положить узел на карте: свободная клетка после существующих. */
  nextPosition: { x: number; y: number };
  onSubmit: (input: {
    kind: NodeKind;
    name: string;
    address: string | null;
    x: number;
    y: number;
  }) => void;
}): JSX.Element {
  const { t } = useTranslation();
  const errorText = useErrorText();

  const [name, setName] = useState('');
  const [address, setAddress] = useState('');
  const [kind, setKind] = useState<NodeKind>('pc');

  function submit(event: FormEvent): void {
    event.preventDefault();
    onSubmit({
      kind,
      name: name.trim(),
      address: address.trim() || null,
      ...nextPosition,
    });
  }

  return (
    <FormRow onSubmit={submit}>
      <div className="w-full sm:w-[200px]">
        <Field
          label={t('nodes.form.name')}
          required
          value={name}
          onChange={(event) => setName(event.target.value)}
          placeholder="SRV-01"
        />
      </div>

      <div className="w-full sm:w-[180px]">
        <Field
          label={t('nodes.field.address')}
          mono
          value={address}
          onChange={(event) => setAddress(event.target.value)}
          placeholder="10.20.0.11"
          hint={t('nodes.form.addressPlaceholder')}
        />
      </div>

      <label className="flex flex-col gap-1.5">
        <span className="text-sm text-muted">{t('nodes.field.kind')}</span>
        <select
          value={kind}
          onChange={(event) => setKind(event.target.value as NodeKind)}
          className="h-[34px] rounded-sm border border-line-strong bg-raised px-2 text-base text-fg"
        >
          {Object.entries(NODE_KIND_LABEL_KEYS).map(([value, key]) => (
            <option key={value} value={value}>
              {t(key)}
            </option>
          ))}
        </select>
      </label>

      <FormActions>
        <Button
          icon="plus"
          type="submit"
          variant="primary"
          size="sm"
          disabled={pending || !name.trim()}
        >
          {pending ? t('sites.form.creating') : t('sites.form.create')}
        </Button>
        {error ? <span className="text-sm text-down-fg">{errorText(error)}</span> : null}
      </FormActions>
    </FormRow>
  );
}

/**
 * Предупреждение о том, что проверки выполнять некому.
 *
 * Разные тексты для «агента нет вовсе» и «агент есть, но не на связи»:
 * в первом случае надо ставить службу, во втором — разбираться со связью,
 * и слить их в одно сообщение значит отправить дежурного не туда.
 */
function AgentWarning({ hasAgent, compact }: { hasAgent: boolean; compact: boolean }): JSX.Element {
  const { t } = useTranslation();

  const title = hasAgent ? t('nodes.agentWarn.offlineTitle') : t('nodes.agentWarn.missingTitle');
  const text = hasAgent ? t('nodes.agentWarn.offlineText') : t('nodes.agentWarn.missingText');

  /*
    На телефоне пять строк объяснения — это треть экрана, отданная тексту
    вместо списка узлов, ради которого сюда и пришли. Поэтому там остаётся
    короткая заметная строка, а объяснение открывается по нажатию.

    Насовсем прятать нельзя: предупреждение, которого не видно, ничего
    не предупреждает. Отсюда и цвет, и то, что подпись стоит рядом
    со значком, а не только под ним.
  */
  if (compact) {
    return (
      <div className="flex flex-none border-b border-line border-l-2 border-l-warn bg-row-alt px-3.5 py-2">
        <InfoNote label={title} summary={title} tone="warn">
          {text}
        </InfoNote>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-1 border-b border-line border-l-2 border-l-warn bg-row-alt px-3.5 py-2.5">
      <div className="text-base font-medium">{title}</div>
      <div className="max-w-[640px] text-sm leading-[1.45] text-muted">{text}</div>
    </div>
  );
}

function StatusChip({
  status,
  count,
  active,
  onToggle,
}: {
  status: Status;
  count: number;
  active: boolean;
  onToggle: (filter: StatusFilter) => void;
}): JSX.Element {
  const { t } = useTranslation();

  return (
    <button
      type="button"
      aria-pressed={active}
      aria-label={t('nodes.statusFilter', { status: t(STATUS_LABEL_KEYS[status]), count })}
      onClick={() => onToggle(active ? 'all' : status)}
      className={[
        'flex h-6 items-center gap-1.5 rounded-sm border px-2 text-sm',
        status === 'down' ? 'border-down-line bg-down-bg text-down-fg' : 'border-line-strong',
        status === 'unknown' ? 'text-muted' : '',
        active ? 'shadow-[inset_0_0_0_1px_var(--nd-text)]' : '',
      ].join(' ')}
    >
      <StatusDot status={status} size={7} />
      {STATUS_SHORT[status]} {count}
    </button>
  );
}

/**
 * Признак живой подписки. Показан рядом с фильтрами намеренно: цифры
 * состояний имеют смысл только если поток статусов идёт.
 */
function LiveIndicator({ connected }: { connected: boolean }): JSX.Element {
  const { t } = useTranslation();

  return (
    <span
      className="flex items-center gap-1.5 font-mono text-xs text-dim"
      title={connected ? t('nodes.live.on') : t('nodes.live.off')}
    >
      <StatusDot
        status={connected ? 'up' : 'unknown'}
        size={7}
        label={connected ? t('nodes.live.active') : t('nodes.live.lost')}
      />
      {connected ? 'live' : t('nodes.live.offline')}
    </span>
  );
}

/**
 * Порядок состояний при сортировке: сперва сломанное.
 *
 * По алфавиту он был бы бессмысленным — «down» встал бы между «unknown»
 * и «up» по букве, а сортируют этот столбец ровно затем, чтобы собрать
 * наверху то, с чем надо разбираться.
 */
const STATUS_ORDER: Record<Status, number> = {
  down: 0,
  warn: 1,
  unknown: 2,
  up: 3,
};

const STATUS_LABEL_KEYS: Record<Status, string> = {
  up: 'nodes.status.up',
  down: 'nodes.status.down',
  warn: 'nodes.status.warn',
  unknown: 'nodes.status.unknown',
};

const STATUS_SHORT: Record<Status, string> = {
  up: 'up',
  down: 'down',
  warn: 'warn',
  unknown: 'unknown',
};

/**
 * Куда положить новый узел на карте.
 *
 * Узел, заведённый из списка, тоже попадает на карту фазы 4, и без координат
 * все такие узлы легли бы в одну точку — карту пришлось бы разбирать вручную.
 * Клетка выбирается по числу существующих узлов: сетка, а не поверх друг друга.
 */
function nextFreeSlot(nodes: readonly NetworkNode[]): { x: number; y: number } {
  const columns = 6;
  const stepX = 208;
  const stepY = 110;
  const index = nodes.length;

  return { x: (index % columns) * stepX, y: Math.floor(index / columns) * stepY };
}
